import {createSelector} from 'reselect';
import {evaluateExperiment} from '../utils/abacusHelper';
import {getTranslations, getListing} from './store';
import {getBrandName, getIsMobile} from './system';
import {isOwnerProfileModalOpen} from './modalStates';
import {getOwnerProfileRoute} from './routes';
import {labelMaker} from '../../server/utils/labelMaker';
import {
    getContact,
    getGeography,
    getHostType,
    getPropertyType,
    getPmProfile,
    getTakesInquiries,
    getThumbnailUrl
} from './listing';
import {getPremierPartnerBadge} from './badges';

const PDP_LEAKAGE_EXPERIMENT = 'remove_partner_name_and_logo_from_PDP';

const getImage = createSelector(
    [getContact, getThumbnailUrl, getPmProfile],
    (contact = {}, thumbnailUrl, pmProfile) => {
        // todo: this is a temporary test to measure if a problem exists, this code will be removed once the test is over. jwang 2019-08-29
        // to test it, append ?abov=32101|1|1 to forcefully bucket into this variant
        const hideContactLogo = evaluateExperiment({
            name: PDP_LEAKAGE_EXPERIMENT,
            variant: 1,
            deferLogging: false
        });

        const ownerPhoto = contact.ownerProfilePhoto;
        const ipmLogo = pmProfile.logoUrl;
        const propertyThumb = thumbnailUrl ? thumbnailUrl.replace('TREATMENT', 'c1') : '';
        const url =
            (!hideContactLogo && ownerPhoto) || (!hideContactLogo && ipmLogo) || propertyThumb;
        const isLogo = !!ipmLogo;

        return {
            url,
            isLogo
        };
    }
);

/* eslint-disable complexity */
const getOwnerDetails = createSelector(
    [
        getListing,
        getTranslations,
        getBrandName,
        getIsMobile,
        isOwnerProfileModalOpen,
        getOwnerProfileRoute,
        getGeography,
        getHostType,
        getPremierPartnerBadge,
        getPropertyType,
        getTakesInquiries,
        getContact,
        getImage
    ],
    (
        listing,
        trans,
        brandName,
        isMobile,
        ownerProfileModalOpen,
        ownerProfileRoute,
        geography,
        hostType,
        premierPartnerBadge,
        propertyType,
        takesInquiries,
        contact = {},
        image
    ) => {
        const {ownersListingProfile} = listing;
        let ownerProfile;
        const contactOwner = hostType.owner ? trans.contactOwner : trans.contactManager;

        const labels = {
            contactOwner,
            languages: `${trans.languages}:`,
            or: trans.or,
            skipToAskQuestion: trans.skipToAskQuestion,
            skipToAskQuestionAnchour: trans.skipToAskQuestionAnchour
        };

        let location = '';
        if (geography && geography.name) {
            location = geography.name;
        }

        // todo: this is a temporary test to measure if a problem exists, this code will be removed once the test is over. jwang 2019-08-29
        // to test it, append ?abov=32101|1|1 to forcefully bucket into this variant
        const hideContactName = evaluateExperiment({
            name: PDP_LEAKAGE_EXPERIMENT,
            variant: 1,
            deferLogging: false
        });
        const fallbackName = hostType.owner ? trans.owner : trans.propertyManager;
        // Todo - Use lingua keys for fallbacks.
        const contactName = (!hideContactName && contact.name) || fallbackName;

        if (ownersListingProfile) {
            ownerProfile = {
                aboutYou: (ownersListingProfile.aboutYou || '').trim(),
                labels: {
                    modalGenericTitle: contactName,
                    title: labelMaker(trans.ownerProfileAbout, contactName),
                    purchased: ownersListingProfile.yearPurchased
                        ? labelMaker(
                              trans.ownerProfilePurchasedIn,
                              contactName,
                              propertyType,
                              ownersListingProfile.yearPurchased
                          )
                        : '',
                    whyPurchased: labelMaker(trans.ownerProfileWhyPurchased, contactName, location),
                    whyUnique: labelMaker(trans.ownerProfileWhyUnique, propertyType),
                    ownerProfileViewMore: labelMaker(trans.ownerProfileViewMore, contactName),
                    ownerProfileViewLess: labelMaker(trans.ownerProfileViewLess, contactName)
                },
                uniqueBenefits: (ownersListingProfile.uniqueBenefits || '').trim(),
                whyHere: (ownersListingProfile.whyHere || '').trim()
            };
        }

        return {
            ctaAtBottom: !!isMobile,
            image: image.url,
            imageIsLogo: image.isLogo,
            inlineProfile: !isMobile,
            labels,
            memberSince: labelMaker(trans.memberSince, contact.memberSince),
            name: contactName,
            ownerProfileModalOpen,
            ownerProfileRoute,
            ownerProfile,
            premierPartner: premierPartnerBadge,
            languages: contact.languagesSpoken ? contact.languagesSpoken.join(', ') : '',
            takesInquiries
        };
    }
);
/* eslint-enable complexity */

export default getOwnerDetails;
export {getImage, getOwnerDetails};
